import { ETL_CONFIGURATION_STATUS, ETL_INTEGRATION_TYPE, Values } from '../../components/configuration/types';

export type ConfigurationType =
  | WebexCCConfigurationType
  | ZoomCCConfigurationType
  | MSTeamsConfigurationType
  | WebexCallingConfigurationType;

export interface BaseConfigurationType {
  id?: number;
  configurationName: string;
  state: Values<typeof ETL_CONFIGURATION_STATUS>;
  integration: Values<typeof ETL_INTEGRATION_TYPE>;

  configurationProperties?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  configurationVariables?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  tasks: ETLTaskType[];
  streams: ETLStreamType[];
}

export interface WebexCCConfigurationType extends BaseConfigurationType {
  integration: typeof ETL_INTEGRATION_TYPE.wxcc;
  configurationProperties?: {
    organizationId: string; // max10000
    wxccClientId: string; // max10000
    wxccClientSecret: string; // max10000
    wxccApiUrl: string; // max10000 and also valid URL
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
  configurationVariables?: {
    wxccAccessToken: string;
    wxccExpiresIn?: number;
    wxccRefreshToken: string;
    wxccRefreshTokenExpiresIn?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  // tasks: [UserSynchronizationTaskType, AdherenceTaskType, HistoricalDataImportTaskType];
  // streams: [CtiStreamType];

  /**
   * Temporary field
   */
  startTime?: number; // to correctly update tokens expiration times on submit
  conflictedRoles?: Record<string, string[]>; // to correctly track state about conflicts in roles
}

export interface ZoomCCConfigurationType extends BaseConfigurationType {
  integration: typeof ETL_INTEGRATION_TYPE.zoomcc;
  configurationProperties?: {
    organizationId: string; // max10000
    zoomccClientId: string; // max10000
    zoomccClientSecret: string; // max10000
    zoomccApiUrl: string; // max10000 and also valid URL
  };
  configurationVariables?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  // tasks: [];
  // streams: [];
}

export interface MSTeamsConfigurationType extends BaseConfigurationType {
  integration: typeof ETL_INTEGRATION_TYPE.msteams;
  configurationProperties?: {
    organizationId: string; // max10000
    msteamsClientId: string; // max10000
    msteamsClientSecret: string; // max10000
    msteamsApiUrl: string; // max10000 and also valid URL
  };
  configurationVariables?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  // tasks: [];
  // streams: [];
}

export interface WebexCallingConfigurationType extends BaseConfigurationType {
  integration: typeof ETL_INTEGRATION_TYPE.wxcalling;
  configurationProperties?: {
    organizationId: string; // max10000
    wxcallingClientId: string; // max10000
    wxcallingClientSecret: string; // max10000
    wxcallingApiUrl: string; // max10000 and also valid URL
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
  configurationVariables?: {
    wxcallingAccessToken: string;
    wxcallingExpiresIn?: number;
    wxcallingRefreshToken: string;
    wxcallingRefreshTokenExpiresIn?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };

  // tasks: [];
  // streams: [];

  /**
   * Temporary field
   */
  startTime?: number; // to correctly update tokens expiration times on submit
  conflictedRoles?: Record<string, string[]>; // to correctly track state about conflicts in roles
}

export type ETLTaskType = UserSynchronizationTaskType | HistoricalDataImportTaskType | AdherenceTaskType;
export type ETLStreamType = RealTimeDataStreamType | MediaImportStreamType;

interface BasicTaskType {
  id?: number;
  scheduled: boolean;
  cronExpression: string; // validated cron expression
  taskVariables?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
  taskProperties?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
}

interface BasicStreamType {
  id?: number;
  enabled: boolean;
  streamName: string;
  streamVariables?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
  streamProperties?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
}

export interface AdherenceTaskType extends BasicTaskType {
  taskName: string; // ReturnType<typeof TASK_NAMES.adherence>
  taskProperties?: {
    lookBackPeriod: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
}

export interface HistoricalDataImportTaskType extends BasicTaskType {
  taskName: string; // ReturnType<typeof TASK_NAMES.historicalData>
  taskProperties?: {
    lookBackPeriod: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
}

export interface UserSynchronizationTaskType extends BasicTaskType {
  taskName: string; // ReturnType<typeof TASK_NAMES.userSynchronization>
  taskProperties?: {
    roleMapping?: Record<string, string[]>;
    groupSupervisors?: string[];
    includeGroups?: string[];
    excludeGroups?: string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // other keys can be defined on BE, but has to be preserved on FE
  };
}

export type RealTimeDataStreamType = BasicStreamType;
export type MediaImportStreamType = BasicStreamType;

export const JOB_NAMES = {
  userSynchronization: 'user-synchronization',
  adherence: 'adherence-data',
  historicalData: 'historical-data',
} as const;

export const STREAM_NAMES = {
  realTimeData: 'rta',
  mediaImport: 'media-import',
} as const;

export const TASK_NAMES = {
  userSynchronization: (type: Values<typeof ETL_INTEGRATION_TYPE>) =>
    `${type}-${JOB_NAMES.userSynchronization}` as const,
  adherence: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `${type}-${JOB_NAMES.adherence}` as const,
  historicalData: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `${type}-${JOB_NAMES.historicalData}` as const,
  /**
   * for now without integration type as prefix
   * `${type}-${JOB_NAMES.userSynchronization}` as const
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  realTimeData: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `${type}-${STREAM_NAMES.realTimeData}` as const,
  mediaImport: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `${type}-${STREAM_NAMES.mediaImport}` as const,
} as const;

export const USER_SYNCHRONIZATION_FIELDS = {
  taskName: 'tasks.0.taskName',
  scheduled: 'tasks.0.scheduled',
  cronExpression: 'tasks.0.cronExpression',
  taskProperties: 'tasks.0.taskProperties',
  taskVariables: 'tasks.0.taskVariables',
  roleMapping: 'tasks.0.taskProperties.roleMapping',
  selectedTeams: 'tasks.0.taskProperties.selectedTeams',
  teamSelectorMode: 'tasks.0.taskProperties.teamSelectorMode',
  importAllTeams: 'tasks.0.taskProperties.importAllTeams',
  roleMappingSelect: (roleId: string) => `tasks.0.taskProperties.roleMapping.${roleId}` as const,
  groupSupervisors: 'tasks.0.taskProperties.groupSupervisors',
} as const;

export const ADHERENCE_FIELDS = {
  taskName: 'tasks.1.taskName',
  scheduled: 'tasks.1.scheduled',
  cronExpression: 'tasks.1.cronExpression',
  taskProperties: 'tasks.1.taskProperties',
  taskVariables: 'tasks.1.taskVariables',
  lookBackPeriod: 'tasks.1.taskProperties.lookBackPeriod',
} as const;

export const HISTORICAL_DATA_FIELDS = {
  taskName: 'tasks.2.taskName',
  scheduled: 'tasks.2.scheduled',
  cronExpression: 'tasks.2.cronExpression',
  taskProperties: 'tasks.2.taskProperties',
  taskVariables: 'tasks.2.taskVariables',
  lookBackPeriod: 'tasks.2.taskProperties.lookBackPeriod',
} as const;

/**
 * Some configurations only use the media import stream, so we should not hardcode array indexes
 */
export const REAL_TIME_DATA_FIELDS = (index: number) => {
  return {
    streamName: `streams.${index}.streamName`,
    enabled: `streams.${index}.enabled`,
    streamProperties: `streams.${index}.streamProperties`,
    streamVariables: `streams.${index}.streamVariables`,
  } as const;
};

export const MEDIA_IMPORT_FIELDS = (index: number) => {
  return {
    streamName: `streams.${index}.streamName`,
    enabled: `streams.${index}.enabled`,
    streamProperties: `streams.${index}.streamProperties`,
    streamVariables: `streams.${index}.streamVariables`,
    azureConnectionStrings: `streams.${index}.streamProperties.azureConnectionStrings`,
    azureConnectionString: (connectionStringIndex: number) =>
      `streams.${index}.streamProperties.azureConnectionStrings.${connectionStringIndex}` as const,
  } as const;
};

export const INTEGRATION_FORM_FIELDS = {
  name: 'configurationName',
  id: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationProperties.${type}ClientId` as const,
  secret: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationProperties.${type}ClientSecret` as const,
  apiUrl: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationProperties.${type}ApiUrl` as const,

  // used by multiple integrations so accountId(zoomcc), organizationId(wxcc, wxcalling) and tenant(teams) is same attribute
  organizationId: 'configurationProperties.organizationId',

  integration: 'integration',
  state: 'state',
  accessToken: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationVariables.${type}AccessToken` as const,
  refreshToken: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationVariables.${type}RefreshToken` as const,
  accessTokenExpires: (type: Values<typeof ETL_INTEGRATION_TYPE>) => `configurationVariables.${type}ExpiresIn` as const,
  refreshTokenExpires: (type: Values<typeof ETL_INTEGRATION_TYPE>) =>
    `configurationVariables.${type}RefreshTokenExpiresIn` as const,
} as const;

export const TEMPORARY_FORM_FIELDS = {
  startTime: 'startTime',
  conflictedRoles: 'conflictedRoles',
} as const;

export type CommonStreamProps = {
  index: number;
};

export const USER_SYNC_IMPORT_TEAMS_OPTIONS = {
  importAllTeams: 'importAllTeams',
  teamsImportFilter: 'teamsImportFilter',
} as const;

export const USER_SYNC_FILTER_TEAMS_OPTIONS = {
  includeSelected: 'includeSelected',
  excludeSelected: 'excludeSelected',
} as const;
